/**
 * BootGate Component
 *
 * Calls Central's switch-context endpoint at boot (after OIDC auth succeeds)
 * to validate workspace status and load entitlements.
 *
 * Auth method: Bearer token from Redux (NOT cookie-include).
 * Endpoint: GET ${VITE_CENTRAL_URL}/api/suite/switch-context
 *
 * Outcomes:
 * - 200: workspace open → store entitlements, render children
 * - 401: token rejected → clear auth, redirect to Central
 * - 409 + code WORKSPACE_CLOSED → show WorkspaceClosedOverlay
 * - Network error / 5xx / timeout → show CentralUnavailableOverlay (fail-closed)
 *
 * IMPORTANT:
 * - No auto-switch, no auto-retry loops.
 * - Timeout (30s) transitions to CentralUnavailableOverlay once (user retries manually).
 * - Workspace switching is manual only (isSwitchingWorkspace blocks boot effect).
 */

import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import { getSimulatedCentralRole } from '../../auth/simulatedCentralRole';
import { getSimulatedAppProfile } from '../../auth/simulatedAppProfile';
import { SCOPE_BUNDLES } from '../../../../shared/src/appRoles';
import {
  selectCentralAccessToken,
  selectIsCentralAuthenticated,
  selectBootCompleted,
  selectWorkspaceClosed,
  selectCentralUnavailable,
  selectIsSwitchingWorkspace,
  setBootReady,
  setBootWorkspaceClosed,
  setBootUnavailable,
  updateCentralAccessToken,
} from '../../store/slices/centralAuthSlice';
import { CENTRAL_URL, APP_ID } from '../../config/centralAuth';
import { WorkspaceClosedOverlay } from '../overlays/WorkspaceClosedOverlay';
import { CentralUnavailableOverlay } from '../overlays/CentralUnavailableOverlay';

interface BootGateProps {
  children: React.ReactNode;
}

/**
 * Permission shape that matches Central's currentWorkspace.permissions.
 * We rebuild it from the simulated role so consumers like
 * useCentralConsumer().hasWorkspacePermission keep working.
 */
function permissionsForRole(role: string): {
  manageMembers: boolean;
  manageBilling: boolean;
  transferOwnership: boolean;
  closeWorkspace: boolean;
} {
  if (role === 'owner') {
    return { manageMembers: true, manageBilling: true, transferOwnership: true, closeWorkspace: true };
  }
  if (role === 'admin') {
    return { manageMembers: true, manageBilling: true, transferOwnership: false, closeWorkspace: false };
  }
  return { manageMembers: false, manageBilling: false, transferOwnership: false, closeWorkspace: false };
}

/**
 * Apply Testing-Mode overrides (sessionStorage) to the workspace object
 * returned by Central's switch-context. Returns the workspace unchanged
 * when no overrides are set.
 *
 * Order of precedence:
 *   1. simulated central role  → workspace.role + workspace.permissions
 *   2. simulated app profile   → workspace.scopes (only when role === 'member')
 *
 * Both overrides are sessionStorage-backed and cleared by the Testing
 * Mode panel's Clear button (and on logout).
 */
function applyTestingModeOverrides<T extends Record<string, unknown>>(workspace: T | null): T | null {
  if (!workspace) return workspace;

  const simulatedRole = getSimulatedCentralRole();
  const simulatedProfile = getSimulatedAppProfile();
  if (!simulatedRole && !simulatedProfile) return workspace;

  const next: Record<string, unknown> = { ...workspace };

  if (simulatedRole) {
    next.role = simulatedRole;
    next.permissions = permissionsForRole(simulatedRole);
  }

  // App-profile override only meaningful when the effective role is 'member'.
  // Owner / admin / viewer have role-driven access; ignore the bundle for them.
  const effectiveRole = (next.role ?? 'member') as string;
  if (simulatedProfile && effectiveRole === 'member') {
    const scopes = new Set<string>();
    for (const profileKey of simulatedProfile) {
      const bundle = SCOPE_BUNDLES[profileKey];
      if (bundle) for (const s of bundle) scopes.add(s);
    }
    next.scopes = Array.from(scopes);
    // Capabilities are derived from scopes by Central; clear the local
    // capabilities map (set to undefined, NOT empty object) so the shared
    // hasCapability() function falls back to exact-scope checks against
    // the simulated scope set. An empty {} is truthy and would make the
    // shared function think capabilities are enabled but find nothing → deny all.
    next.capabilities = undefined;
  }

  return next as T;
}

export function BootGate({ children }: BootGateProps) {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector(selectCentralAccessToken);
  const isAuthenticated = useSelector(selectIsCentralAuthenticated);
  const bootCompleted = useSelector(selectBootCompleted);
  const workspaceClosed = useSelector(selectWorkspaceClosed);
  const centralUnavailable = useSelector(selectCentralUnavailable);
  const isSwitchingWorkspace = useSelector(selectIsSwitchingWorkspace);

  useEffect(() => {
    // Only call switch-context when authenticated and not yet booted.
    // Skip if workspace is closed, Central is unavailable, or a manual switch is in progress.
    if (!isAuthenticated || !accessToken || bootCompleted || workspaceClosed || centralUnavailable || isSwitchingWorkspace) return;

    let cancelled = false;
    const controller = new AbortController();
    // 30s generous timeout → transitions to CentralUnavailableOverlay (one-shot, no loop)
    const timeout = setTimeout(() => controller.abort(), 30_000);

    (async () => {
      try {
        const response = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'X-App-Id': APP_ID,
          },
          signal: controller.signal,
        });

        clearTimeout(timeout);
        if (cancelled) return;

        if (response.ok) {
          const data = await response.json();
          // Update token if switch-context returns a new one
          if (data.accessToken) {
            dispatch(updateCentralAccessToken({
              accessToken: data.accessToken,
              expiresIn: data.expiresIn,
            }));
          }

          // Testing-Mode: apply simulated Central role + simulated BeHeard
          // app-profile bundle BEFORE storing in Redux. Central does not
          // re-mint the simulating user's switch-context response, so without
          // this shim the sidebar / settings / role badge / useAppRole all
          // keep showing the tester's REAL role/scopes. Pitchless uses the
          // same pattern (admin-frontend/src/central/BootGateProvider.tsx).
          const simulatedWorkspace = applyTestingModeOverrides(data.workspace);

          dispatch(
            setBootReady({
              currentWorkspace: simulatedWorkspace,
              otherWorkspaces: data.otherWorkspaces ?? [],
              apps: data.apps ?? [],
              user: data.user ?? null,
              meta: data.meta,
            })
          );
          return;
        }

        // 401 from switch-context: the token we just received from Central's
        // OIDC flow was rejected by switch-context. Clearing auth here
        // would cause an infinite loop (logout → login → Central OIDC →
        // back here → 401 again), so we surface a fail-closed overlay
        // instead and let the user recover manually. A real session expiry
        // is handled by baseQueryWithReauth on regular API calls, not here.
        if (response.status === 401) {
          console.error('[BootGate] switch-context returned 401 for a fresh OIDC token');
          dispatch(setBootUnavailable('Central rejected the session token'));
          return;
        }

        // Exact-match 409 WORKSPACE_CLOSED
        if (response.status === 409) {
          const data = await response.json().catch(() => ({}));
          if (data.code === 'WORKSPACE_CLOSED') {
            dispatch(setBootWorkspaceClosed({ otherWorkspaces: data.otherWorkspaces ?? [] }));
            return;
          }
        }

        // Any other non-2xx → fail-closed
        console.error('[BootGate] Central returned non-2xx:', response.status);
        dispatch(setBootUnavailable('Central returned non-2xx'));
      } catch (err) {
        clearTimeout(timeout);
        if (cancelled) return;
        console.error('[BootGate] Central unreachable:', err);
        dispatch(setBootUnavailable('Central unreachable'));
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [isAuthenticated, accessToken, bootCompleted, workspaceClosed, centralUnavailable, isSwitchingWorkspace, dispatch]);

  // Show overlays (CentralUnavailable takes precedence at z-[9999])
  if (centralUnavailable) {
    return <CentralUnavailableOverlay />;
  }

  if (workspaceClosed) {
    return <WorkspaceClosedOverlay />;
  }

  // Switching workspace — full-screen blocking overlay (manual switch in progress).
  // MUST be checked BEFORE !isAuthenticated so a transient auth-state race
  // (from the reauth middleware) doesn't hide the switching overlay.
  if (isSwitchingWorkspace) {
    return (
      <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto" />
          <p className="mt-4 text-white text-lg font-medium">Switching workspace…</p>
        </div>
      </div>
    );
  }

  // Not authenticated → don't render children (AuthGuard will redirect)
  if (!isAuthenticated) {
    return null;
  }

  // Show loading while boot is pending
  if (!bootCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-500">Verifying workspace…</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
